import React, { useEffect, useMemo, useState } from "react";
import {
  MaterialReactTable,
  type MRT_ColumnDef,
  type MRT_RowSelectionState,
} from "material-react-table";
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Grid,
  TextField,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  Tooltip,
  RadioGroup,
  Radio,
} from "@mui/material";
import {
  CheckCircle,
  Cancel,
  UploadFile,
  Close,
  LinkOff,
  GetApp,
  Undo,
  Download,
} from "@mui/icons-material";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import {
  fetchBackOfficeTrades,
  BackOfficeTrade,
  bulkChangeTradeStatus,
  fetchUserDetails,
  UserDetails,
} from "../../../api/backOfficeService";
import axiosInstance from "../../../Utils/axiosInstance";
import { useNavigate } from "react-router-dom";


// ---- Stub for status update (replace with your real API call) ----
const updateTradesStatus = async (
  ids: string[],
  newStatus: string
): Promise<void> => {
  console.log("Updating trades:", ids, "to", newStatus);
  // TODO: replace with API call → await api.updateStatus(ids, newStatus)
};
export type TradeStatus =
  | "pending"
  | "committed"
  | "validated"
  | "approved"
  | "rejected";
const statusColors: Record<string, { bg: string; color: string }> = {
  pending: { bg: "#ffe0b2", color: "#e65100" },
  committed: { bg: "#bbdefb", color: "#0d47a1" },
  validated: { bg: "#c8e6c9", color: "#1b5e20" },
  approved: { bg: "#d1c4e9", color: "#4a148c" },
  rejected: { bg: "#ffcdd2", color: "#b71c1c" },
};

const isTradeAdminOrOrgAdmin = (user: UserDetails | null): boolean =>
  user?.user_type === "organization_admin" ||
  user?.user_group === "trade_admin";
const ConfirmDialog = ({
  open,
  onClose,
  onConfirm,
  status,
  multiple,
  user,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  status: TradeStatus;
  multiple?: boolean;
  user: UserDetails | null;
}) => (
  <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
    <DialogTitle>
      {isTradeAdminOrOrgAdmin(user)
        ? status === "validated"
          ? "Validate"
          : "Reject"
        : status === "committed"
          ? "Commit"
          : "Revert to Pending"}{" "}
      {multiple ? "Trades" : "Trade"}
    </DialogTitle>
    <DialogContent>
      <Typography variant="body2">
        Are you sure you want to{" "}
        <b style={{ textTransform: "capitalize" }}>
          {status === "rejected"
            ? "reject"
            : status === "validated"
              ? "validate"
              : status}
        </b>{" "}
        {multiple ? "the selected trades?" : "this trade?"}
      </Typography>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose} size="small">
        Cancel
      </Button>
      <Button
        onClick={onConfirm}
        variant="contained"
        size="small"
        color={
          isTradeAdminOrOrgAdmin(user)
            ? status === "validated"
              ? "success"
              : "error"
            : status === "committed"
              ? "info"
              : "warning"
        }
      >
        {isTradeAdminOrOrgAdmin(user)
          ? status === "validated"
            ? "Validate"
            : "Reject"
          : status === "committed"
            ? "Commit"
            : "Pending"}
      </Button>
    </DialogActions>
  </Dialog>
);

const ReconciliationTable: React.FC = () => {
  const navigate = useNavigate();
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 25,
  });
  const [totalCount, setTotalCount] = useState(0);
  const [data, setData] = useState<BackOfficeTrade[]>([]);
  const [filteredData, setFilteredData] = useState<BackOfficeTrade[]>([]);
  const [uploadedData, setUploadedData] = useState<any[]>([]);
  const [uploadedColumns, setUploadedColumns] = useState<MRT_ColumnDef<any>[]>(
    []
  );
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
  const [loading, setLoading] = useState(false);
  const [uploadedRowSelection, setUploadedRowSelection] =
    useState<MRT_RowSelectionState>({});

  const [viewType, setViewType] = useState<'organization' | 'counterparty'>('organization');
  const [confirmState, setConfirmState] = useState<{
    open: boolean;
    ids: string[];
    status: TradeStatus;
  }>({ open: false, ids: [], status: "pending" });
  const [user, setUser] = useState<UserDetails | null>(null);

  const [rejectedPairs, setRejectedPairs] = useState<
    { uploadedId: string; backendId: string }[]
  >([]);

  const openConfirm = (ids: string[], status: TradeStatus) =>
    setConfirmState({ open: true, ids, status });

  const closeConfirm = () =>
    setConfirmState((prev) => ({ ...prev, open: false }));

  const handleConfirm = async () => {
    await handleChangeStatus(confirmState.ids, confirmState.status);
    setUploadedRowSelection({});
    setRowSelection({});
    closeConfirm();
  };

  const handleViewTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newViewType = event.target.value as 'organization' | 'counterparty';
    setViewType(newViewType);

    // Navigate based on selection
    if (newViewType === 'organization') {
      navigate(`${process.env.PUBLIC_URL}/trading/trade-reconciliation`);
    } else if (newViewType === 'counterparty') {
      navigate(`${process.env.PUBLIC_URL}/trading/trade-reconciliation-counterparty`);
    }
  };
  // -------- Date Filter State --------
  const [dateUsage, setDateUsage] = useState("formatted_date");
  const [dateRange, setDateRange] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [applyDateToUploaded, setApplyDateToUploaded] = useState(false);

  const [matchedPairs, setMatchedPairs] = useState<
    { backendId: string; uploadedId: string }[]
  >([]);

  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [bookFilter, setBookFilter] = useState<string[]>([]);
  const [commodityFilter, setCommodityFilter] = useState<string[]>([]);

  // --- Define Columns for main table ---
  const columns: MRT_ColumnDef<BackOfficeTrade>[] = useMemo(
    () => [
      {
        id: "slno",
        header: "Sl No.",
        size: 40,
        minSize: 30,
        enableColumnFilter: false,
        Cell: ({ row }) => row.index + 1,
      },
      {
        accessorKey: "status",
        header: "Status",
        size: 60, // reduced width
        minSize: 50, // smaller minimum
        maxSize: 80, // optional max
        enableColumnFilter: true, // removes the dropdown filter
        Cell: ({ cell }) => {
          const value = (cell.getValue() as string) || "";
          const normalized = value.toLowerCase();

          let bgColor = "";
          let textColor = "#000";
          if (normalized === "pending") {
            bgColor = "#ffe0b2";
            textColor = "#e65100";
          } else if (normalized === "rejected") {
            bgColor = "#ffcdd2";
            textColor = "#b71c1c";
          } else if (normalized === "validated") {
            bgColor = "#c8e6c9";
            textColor = "#1b5e20";
          } else if (normalized === "committed") {
            bgColor = "#bbdefb"; // light blue background
            textColor = "#0d47a1"; // dark blue text
          }

          return (
            <Box
              sx={{
                display: "inline-block",
                px: 0.5, // smaller horizontal padding
                py: 0.1, // smaller vertical padding
                borderRadius: "8px",
                fontSize: "0.6rem", // smaller text
                fontWeight: 600,
                backgroundColor: bgColor,
                color: textColor,
                textTransform: "capitalize",
                textAlign: "center",
              }}
            >
              {value}
            </Box>
          );
        },
      },
      {
        accessorKey: "formatted_date",
        header: "Trade Date",
        size: 80,
        minSize: 70,
      },
      { accessorKey: "exec_id", header: "Trade ID", size: 80, minSize: 60 },
      { accessorKey: "leg_id", header: "Leg ID", size: 60, minSize: 50 },
      {
        accessorKey: "book_name_resolved",
        header: "Book",
        size: 80,
        minSize: 70,
      },
      {
        accessorKey: "commodity_name",
        header: "Commodity",
        size: 80,
        minSize: 70,
      },
      { accessorKey: "quantity", header: "Quantity", size: 50, minSize: 40 },
      { accessorKey: "unit_name", header: "Unit", size: 50, minSize: 40 },
      {
        accessorKey: "pricing_from_formatted",
        header: "Pricing From",
        size: 80,
        minSize: 60,
      },
      {
        accessorKey: "pricing_to_formatted",
        header: "Pricing To",
        size: 80,
        minSize: 60,
      },
      { accessorKey: "approx_exec_price", header: "Approx Exec Price", size: 60, minSize: 50 },
      { accessorKey: "clearing_account_name", header: "Clearing Account", size: 60, minSize: 50 },
      { accessorKey: "customer_company_name_resolved", header: "Customer Company", size: 60, minSize: 50 },
      { accessorKey: "price", header: "Price", size: 60, minSize: 50 },
      {
        accessorKey: "updated_at",
        header: "Updated Date",
        size: 80,
        minSize: 70,
      },
      {
        accessorKey: "updated_by",
        header: "Updated By",
        size: 70,
        minSize: 60,
      },
    ],
    []
  );

  // --- Load Data from API ---
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const page = pagination.pageIndex + 1; // API starts from 1
        const response = await axiosInstance.get(`/paper-app/back-office?page=${page}`);

        setData(response.data.results);
        setTotalCount(response.data.count);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [pagination.pageIndex]);

  useEffect(() => {
    const loadUser = async () => {
      try {
        const userData = await fetchUserDetails();
        setUser(userData);
      } catch (err) {
        console.error("Failed to fetch user", err);
      }
    };

    loadUser();
  }, []);
  // 1. Restore filters on mount (runs only once)
  useEffect(() => {
    const saved = localStorage.getItem("reconciliationFilters");
    if (saved) {
      const parsed = JSON.parse(saved);
      setStatusFilter(parsed.statusFilter || "all");
      setBookFilter(parsed.bookFilter || "all");
      setCommodityFilter(parsed.commodityFilter || "all");
      setDateUsage(parsed.dateUsage || "formatted_date");
      setDateRange(parsed.dateRange || "all");
      setDateFrom(parsed.dateFrom || "");
      setDateTo(parsed.dateTo || "");
    }
  }, []);

  // 2. Re-apply filters whenever data OR any filter changes
  useEffect(() => {
    applyDateFilter();
  }, [
    data,
    statusFilter,
    bookFilter,
    commodityFilter,
    dateUsage,
    dateRange,
    dateFrom,
    dateTo,
  ]);

  // 3. Save filters to localStorage whenever they change
  useEffect(() => {
    const filters = {
      statusFilter,
      bookFilter,
      commodityFilter,
      dateUsage,
      dateRange,
      dateFrom,
      dateTo,
    };
    localStorage.setItem("reconciliationFilters", JSON.stringify(filters));
  }, [
    statusFilter,
    bookFilter,
    commodityFilter,
    dateUsage,
    dateRange,
    dateFrom,
    dateTo,
  ]);





  const handleApply = () => {
    const filters = {
      statusFilter,
      bookFilter,
      commodityFilter,
      dateUsage,
      dateRange,
      dateFrom,
      dateTo,
    };
    localStorage.setItem("reconciliationFilters", JSON.stringify(filters));
    applyDateFilter();
  };

  const handleClear = () => {
    setStatusFilter([]); // reset to no selection → defaults will apply automatically
    setBookFilter([]); // empty array
    setCommodityFilter([]); // empty array
    setDateUsage("formatted_date");
    setDateRange("all");
    setDateFrom("");
    setDateTo("");

    // Clear localStorage
    localStorage.removeItem("reconciliationFilters");

    setFilteredData(data);
  };

  const statusOptions = [
    "pending",
    "committed",
    "validated",
    "approved",
    "rejected",
  ];

  const uniqueBooks = useMemo(() => {
    const books = data.map((t) => t.book_name_resolved).filter(Boolean);
    return Array.from(new Set(books));
  }, [data]);

  const uniqueCommodities = useMemo(() => {
    const commodities = data.map((t) => t.commodity_name).filter(Boolean);
    return Array.from(new Set(commodities));
  }, [data]);

  // --- Handle File Upload ---
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const fileType = file.name.split(".").pop()?.toLowerCase();

    const normalizeData = (rows: any[]) => {
      if (rows.length === 0) return { cols: [], data: [] };

      // normalize headers -> lowercase
      const cols = Object.keys(rows[0]).map((key) => ({
        accessorKey: key.toLowerCase(),
        header: key,
      }));

      // normalize rows -> lowercase keys
      const normalizedRows = rows.map((row) => {
        const obj: any = {};
        Object.keys(row).forEach((k) => {
          obj[k.toLowerCase()] = row[k];
        });
        return obj;
      });

      return { cols, data: normalizedRows };
    };

    if (fileType === "csv") {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: (result) => {
          const rows = result.data as any[];
          const { cols, data } = normalizeData(rows);
          setUploadedColumns(cols);
          setUploadedData(data);
        },
      });
    } else if (fileType === "xlsx" || fileType === "xls") {
      const reader = new FileReader();
      reader.onload = (e) => {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const sheet = XLSX.utils.sheet_to_json<Record<string, any>>(
          workbook.Sheets[sheetName],
          { defval: "" }
        );

        const { cols, data: normalized } = normalizeData(sheet);
        setUploadedColumns(cols);
        setUploadedData(normalized);
      };
      reader.readAsArrayBuffer(file);
    } else {
      alert("Unsupported file type. Please upload CSV or Excel.");
    }
  };

  // --- Updated handleChangeStatus to also mark uploaded table ---
  const handleChangeStatus = async (ids: string[], newStatus: TradeStatus) => {
    try {
      setLoading(true);

      // ✅ Convert selected row IDs → exec_ids
      const execIds = filteredData
        .filter((row) => ids.includes(row.id))
        .map((row) => row.exec_id)
        .filter(Boolean); // remove undefined/null exec_ids

      if (execIds.length === 0) {
        console.warn("No exec_ids found for selected rows");
        return;
      }

      // ✅ Send exec_ids to backend
      await bulkChangeTradeStatus(execIds, newStatus);

      // 🔁 Update local table UI
      setData((prev) =>
        prev.map((t: any) =>
          execIds.includes(t.exec_id) ? { ...t, status: newStatus } : t
        )
      );

      setFilteredData((prev) =>
        prev.map((t: any) =>
          execIds.includes(t.exec_id) ? { ...t, status: newStatus } : t
        )
      );

      // ✅ Uploaded table logic (unchanged)
      const selectedUploadedKeys = Object.keys(uploadedRowSelection);
      if (selectedUploadedKeys.length > 0) {
        if (newStatus === "validated") {
          setMatchedPairs((prev) => [
            ...prev,
            ...selectedUploadedKeys.map((key) => ({
              backendId: execIds[0],
              uploadedId: key,
            })),
          ]);

          setUploadedData((prev) =>
            prev.map((row, index) => {
              if (selectedUploadedKeys.includes(index.toString())) {
                const backendRow = filteredData.find(
                  (b) => b.exec_id === execIds[0]
                );
                return {
                  ...row,
                  status: "validated",
                  matchedValue: row.exec_id || row["trade_id"],
                  referenceId: backendRow?.exec_id || "",
                };
              }
              return row;
            })
          );
        } else if (newStatus === "rejected") {
          setRejectedPairs((prev) => [
            ...prev,
            ...selectedUploadedKeys.map((key) => ({
              backendId: execIds[0],
              uploadedId: key,
            })),
          ]);

          setUploadedData((prev) =>
            prev.map((row, index) => {
              if (selectedUploadedKeys.includes(index.toString())) {
                return {
                  ...row,
                  status: "rejected",
                  referenceId: "Rejected",
                };
              }
              return row;
            })
          );
        }

        setUploadedRowSelection({});
      }
    } catch (err) {
      console.error("Failed to update status", err);
    } finally {
      setLoading(false);
    }
  };


  const handleRevertUploadedRow = (uploadedId: string) => {
    setMatchedPairs((prev) => prev.filter((p) => p.uploadedId !== uploadedId));

    setRejectedPairs((prev) => prev.filter((p) => p.uploadedId !== uploadedId));

    setUploadedData((prev) =>
      prev.map((row, index) =>
        index.toString() === uploadedId
          ? { ...row, status: undefined, referenceId: "" }
          : row
      )
    );

    // also remove row selection if needed
    setUploadedRowSelection((prev) => {
      const newSelection = { ...prev };
      delete newSelection[uploadedId];
      return newSelection;
    });
  };
  const downloadUploadedData = async (
    data: any[],
    columns: MRT_ColumnDef<any>[],
    matchedPairs: { backendId: string; uploadedId: string }[]
  ) => {
    if (!data || data.length === 0) return;

    const ExcelJS = (await import('exceljs')).default;
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Uploaded Data');

    // Add headers
    const headers = columns.map((col) => col.header);
    worksheet.addRow(headers);

    // Style the header row
    worksheet.getRow(1).font = { bold: true };
    worksheet.getRow(1).fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFE0E0E0' }, // light gray
    };

    // Add data rows
    data.forEach((row, index) => {
      const rowData = columns.map((col) => {
        const key = col.accessorKey as string;
        return row[key] || "";
      });
      const excelRow = worksheet.addRow(rowData);

      // Check if this row is matched or rejected
      const uploadedId = index.toString();
      const isMatched = matchedPairs.some((p) => p.uploadedId === uploadedId);
      const isRejected = rejectedPairs.some((p) => p.uploadedId === uploadedId);

      if (isMatched) {
        // Apply light green fill to the entire row (matched)
        excelRow.eachCell({ includeEmpty: true }, (cell) => {
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFC8E6C9' }, // light green - same as table
          };
          cell.font = { bold: true };
        });
      } else if (isRejected) {
        // Apply light red fill to the entire row (rejected)
        excelRow.eachCell({ includeEmpty: true }, (cell) => {
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFFFCDD2' }, // light red - same as table
          };
          cell.font = { bold: true };
        });
      }
    });
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `uploaded_data_${new Date().toISOString().split('T')[0]}.xlsx`;
    link.click();
    window.URL.revokeObjectURL(url);
  };
  const uploadedColumnsWithMatch: MRT_ColumnDef<any>[] = [
    // --- Revert Column FIRST ---
    ...uploadedColumns.map(col => ({
      ...col,
      size: col.accessorKey === "Sl" ? 50 : 120,   // 👈 SL column reduced
      maxSize: col.accessorKey === "sl" ? 60 : 150,
      minSize: col.accessorKey === "sl" ? 40 : 80,
      grow: false,
    })),
    {
      accessorKey: "referenceId",
      header: "Reference ID",
      Cell: ({ row }) => {
        const matches = matchedPairs.filter(
          (p) => p.uploadedId === row.index.toString()
        );
        if (matches.length === 0) return "";
        const backendExecIds = matches
          .map((p) => {
            const backendRow = filteredData.find((b) => b.id === p.backendId);
            return backendRow?.exec_id || "";
          })
          .filter(Boolean);
        return backendExecIds.join(", ");
      },
    },
  ];

  // --- Date Filter Apply ---
  const applyDateFilter = () => {
    let filter: BackOfficeTrade[] = Array.isArray(data) ? [...data] : [];

    const toArray = (v: any) => {
      if (Array.isArray(v)) return v;
      if (typeof v === "string") {
        if (v === "all" || v === "") return [];
        return [v];
      }
      return [];
    };

    const normalize = (arr: any[]) =>
      arr.map((x) => String(x || "").toLowerCase().trim()).filter(Boolean);

    const statusArr = normalize(toArray(statusFilter));
    const bookArr = normalize(toArray(bookFilter));
    const commodityArr = normalize(toArray(commodityFilter));

    // 1) Status filter (with defaults for admin vs non-admin)
    if (statusArr.length === 0) {
      if (isTradeAdminOrOrgAdmin(user)) {
        filter = filter.filter(
          (t) => String(t.status || "").toLowerCase() === "committed"
        );
      } else {
        filter = filter.filter((t) => {
          const s = String(t.status || "").toLowerCase();
          return s === "pending" || s === "rejected";
        });
      }
    } else {
      filter = filter.filter((t) =>
        statusArr.includes(String(t.status || "").toLowerCase())
      );
    }

    // 2) Book filter (normalized)
    if (bookArr.length > 0) {
      filter = filter.filter((t) =>
        bookArr.includes(String(t.book_name_resolved || "").toLowerCase().trim())
      );
    }

    // 3) Commodity filter (normalized)
    if (commodityArr.length > 0) {
      filter = filter.filter((t) =>
        commodityArr.includes(String(t.commodity_name || "").toLowerCase().trim())
      );
    }

    // 4) Date range calculation (safe, non-mutating)
    let start = new Date("1900-01-01T00:00:00.000Z");
    let end = new Date("2999-12-31T23:59:59.999Z");
    const now = new Date();

    if (dateRange === "today") {
      start = new Date(now);
      start.setHours(0, 0, 0, 0);
      end = new Date(now);
      end.setHours(23, 59, 59, 999);
    } else if (dateRange === "thisMonth") {
      start = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
      end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
    } else if (dateRange === "thisWeek") {
      // Week start = Sunday
      const day = now.getDay(); // 0 (Sun) - 6 (Sat)
      start = new Date(now);
      start.setDate(now.getDate() - day);
      start.setHours(0, 0, 0, 0);
      end = new Date(start);
      end.setDate(start.getDate() + 6);
      end.setHours(23, 59, 59, 999);
    } else if (dateRange === "ytd") {
      start = new Date(now.getFullYear(), 0, 1, 0, 0, 0, 0);
      end = new Date(now);
      end.setHours(23, 59, 59, 999);
    } else if (dateRange === "custom" && dateFrom && dateTo) {
      const f = new Date(dateFrom);
      const t = new Date(dateTo);
      if (!isNaN(f.getTime()) && !isNaN(t.getTime())) {
        start = new Date(f);
        start.setHours(0, 0, 0, 0);
        end = new Date(t);
        end.setHours(23, 59, 59, 999);
      }
    }

    // choose which date key to use
    const usageKey =
      dateUsage === "pricing_from"
        ? "pricing_from_formatted"
        : dateUsage === "updated"
          ? "updated_at"
          : "formatted_date";

    // 5) Apply date filtering
    filter = filter.filter((t) => {
      const rawDate = (t as any)[usageKey];
      if (!rawDate) return true; // keep rows that have no date value (same as before)
      const d = new Date(rawDate);
      if (isNaN(d.getTime())) return false;
      return d >= start && d <= end;
    });

    setFilteredData(filter);
  };

  const filteredUploadedData = useMemo(() => {
    if (!applyDateToUploaded) return uploadedData;

    return uploadedData.filter((row: any) => {
      const rowDate = new Date(row[dateUsage]); // same key as main table
      if (!rowDate || isNaN(rowDate.getTime())) return false;

      if (dateRange === "today") {
        const today = new Date();
        return rowDate.toDateString() === today.toDateString();
      }
      if (dateRange === "thisWeek") {
        const now = new Date();
        const start = new Date(now.setDate(now.getDate() - now.getDay()));
        const end = new Date(now.setDate(start.getDate() + 6));
        return rowDate >= start && rowDate <= end;
      }
      if (dateRange === "thisMonth") {
        const now = new Date();
        return (
          rowDate.getMonth() === now.getMonth() &&
          rowDate.getFullYear() === now.getFullYear()
        );
      }
      if (dateRange === "ytd") {
        const now = new Date();
        const start = new Date(now.getFullYear(), 0, 1);
        return rowDate >= start && rowDate <= now;
      }
      if (dateRange === "custom" && dateFrom && dateTo) {
        const from = new Date(dateFrom);
        const to = new Date(dateTo);
        return rowDate >= from && rowDate <= to;
      }
      return true; // "all"
    });
  }, [
    uploadedData,
    applyDateToUploaded,
    dateUsage,
    dateRange,
    dateFrom,
    dateTo,
  ]);

  // --- Summary Stats ---
  const pendingQuantity = useMemo(
    () =>
      filteredData
        .filter((t) => t.status?.toLowerCase() === "pending")
        .reduce((sum, t) => sum + (t.quantity || 0), 0),
    [filteredData]
  );

  const selectedQuantity = useMemo(() => {
    const selectedKeys = Object.keys(rowSelection);
    return selectedKeys.reduce(
      (sum, key) => sum + (filteredData[parseInt(key)]?.quantity || 0),
      0
    );
  }, [rowSelection, filteredData]);

  const sourceUploadedData = applyDateToUploaded
    ? filteredUploadedData
    : uploadedData;

  const pendingUploadedQty = sourceUploadedData
    .filter((t: any) => String(t.status || "").toLowerCase() === "pending")
    .reduce((sum, t: any) => sum + (Number(t.quantity) || 0), 0);

  const selectedUploadedQty = Object.keys(uploadedRowSelection).reduce(
    (sum, key) => {
      const rowIndex = parseInt(key);
      return sum + (sourceUploadedData[rowIndex]?.quantity || 0);
    },
    0
  );

  const verificationStats = {
    committed: data
      .filter((t) => String(t.status).toLowerCase() === "committed")
      .reduce((sum, t) => sum + (t.quantity || 0), 0),

    validated: data
      .filter((t) => String(t.status).toLowerCase() === "validated")
      .reduce((sum, t) => sum + (t.quantity || 0), 0),

    rejected: data
      .filter((t) => String(t.status).toLowerCase() === "rejected")
      .reduce((sum, t) => sum + (t.quantity || 0), 0),

    pending: data
      .filter((t) => String(t.status).toLowerCase() === "pending")
      .reduce((sum, t) => sum + (t.quantity || 0), 0),
  };
  const formatShortDate = (value?: string) => {
    if (!value) return "";
    const date = new Date(value);
    if (isNaN(date.getTime())) return value;

    return date
      .toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "2-digit",
      })
      .replace(/ /g, "-");
  };
  const exportToCSV = () => {
    if (!filteredData || filteredData.length === 0) {
      alert("No data to export");
      return;
    }

    // ❗ Exclude non-data columns like Sl No.
    const exportableColumns = columns.filter(
      (col) => col.accessorKey
    );

    // Headers from column definitions
    const headers = exportableColumns.map(
      (col) => col.header as string
    );

    // Rows
    const csvRows = filteredData.map((row: any) =>
      exportableColumns.map((col) => {
        const key = col.accessorKey as string;
        let value = row[key];

        // Date formatting for date columns
        if (
          key === "formatted_date" ||
          key === "pricing_from_formatted" ||
          key === "pricing_to_formatted" ||
          key === "updated_at"
        ) {
          return formatShortDate(value);
        }

        return value ?? "";
      })
    );

    // CSV string
    const csvContent = [
      headers.join(","),
      ...csvRows.map((row) =>
        row
          .map((cell) => {
            const str = String(cell);
            return str.includes(",") || str.includes('"') || str.includes("\n")
              ? `"${str.replace(/"/g, '""')}"`
              : str;
          })
          .join(",")
      ),
    ].join("\n");

    // Download
    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });

    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `backoffice_trades_${new Date()
      .toISOString()
      .slice(0, 10)}.csv`;

    link.click();
  };

  return (
    <div className="page-body">
      <Box
        sx={{
          mb: 0,
          display: "flex",
          alignItems: "flex-start",
          gap: 1.5,
          mt: -2.5,
        }}
      >
        {/* ---- Date Filter Controls ---- */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            flex: 1, // Takes available space
            "& .MuiInputBase-root": {
              height: 32,
              fontSize: "0.75rem",
            },
            "& .MuiInputLabel-root": {
              fontSize: "0.7rem",
            },
            "& input": {
              fontSize: "0.75rem",
              padding: "4px 6px",
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              borderBottom: '1px solid #e0e0e0',
              pb: 1.5,
            }}
          >
            <Box sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#666' }}>
              View By:
            </Box>
            <RadioGroup
              row
              value={viewType}
              onChange={handleViewTypeChange}
              sx={{
                display: 'flex',
                gap: 0.5,
                '& .MuiRadio-root': {
                  p: 0.5,
                },
                '& .MuiFormControlLabel-root': {
                  m: 0,
                  mr: 1,
                },
              }}
            >
              <FormControlLabel
                value="organization"
                control={
                  <Radio
                    size="small"
                    sx={{
                      '&.Mui-checked': {
                        color: '#1976d2',
                      },
                    }}
                  />
                }
                label={
                  <Box sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
                    Organization
                  </Box>
                }
              />
              <FormControlLabel
                value="counterparty"
                control={
                  <Radio
                    size="small"
                    sx={{
                      '&.Mui-checked': {
                        color: '#1976d2',
                      },
                    }}
                  />
                }
                label={
                  <Box sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
                    Counterparty
                  </Box>
                }
              />
            </RadioGroup>
          </Box>
          {/* --- Filters Row --- */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.8,
              flexWrap: "wrap",
              justifyContent: "flex-start",
            }}
          >
            {/* Status Multi-Select */}
            <Autocomplete
              multiple
              size="small"
              options={statusOptions}
              value={statusFilter}
              onChange={(_, v) => setStatusFilter(v)}
              getOptionLabel={(option) =>
                option.charAt(0).toUpperCase() + option.slice(1).toLowerCase()
              }
              sx={{ minWidth: 120 }}
              slotProps={{
                paper: {
                  sx: {
                    "& .MuiAutocomplete-option": {
                      fontSize: "0.7rem",
                      minHeight: "28px",
                      padding: "2px 8px",
                    },
                  },
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Status"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    "& .MuiInputBase-input": {
                      fontSize: "0.7rem",
                      padding: "4px 6px",
                    },
                    "& .MuiInputLabel-root": { fontSize: "0.7rem" },
                  }}
                />
              )}
            />

            {/* Book Multi-Select */}
            <Autocomplete
              multiple
              size="small"
              options={uniqueBooks}
              value={bookFilter}
              onChange={(_, v) => setBookFilter(v)}
              sx={{ minWidth: 120 }}
              slotProps={{
                paper: {
                  sx: {
                    "& .MuiAutocomplete-option": {
                      fontSize: "0.7rem",
                      minHeight: "28px",
                      padding: "2px 8px",
                    },
                  },
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Book"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />

            {/* Commodity Multi-Select */}
            <Autocomplete
              multiple
              size="small"
              options={uniqueCommodities}
              value={commodityFilter}
              onChange={(_, v) => setCommodityFilter(v)}
              sx={{ minWidth: 120 }}
              slotProps={{
                paper: {
                  sx: {
                    "& .MuiAutocomplete-option": {
                      fontSize: "0.7rem",
                      minHeight: "28px",
                      padding: "2px 8px",
                    },
                  },
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Commodity"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />

            {/* Date Usage */}
            <TextField
              select
              label="Date Usage"
              size="small"
              value={dateUsage}
              onChange={(e) => setDateUsage(e.target.value)}
              sx={{
                minWidth: 120,
                "& .MuiSelect-select": {
                  fontSize: "0.7rem",
                  padding: "4px 6px",
                },
              }}
              SelectProps={{
                MenuProps: {
                  PaperProps: {
                    sx: {
                      "& .MuiMenuItem-root": {
                        fontSize: "0.7rem",
                        minHeight: "28px",
                        padding: "2px 8px",
                      },
                    },
                  },
                },
              }}
            >
              {[
                { value: "formatted_date", label: "Trade Date" },
                { value: "pricing_from", label: "Pricing From" },
                { value: "updated", label: "Updated Date" },
              ].map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>

            {/* Date Range */}
            <TextField
              select
              label="Date Range"
              size="small"
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              sx={{
                minWidth: 120,
                "& .MuiSelect-select": {
                  fontSize: "0.7rem",
                  padding: "4px 6px",
                },
              }}
              SelectProps={{
                MenuProps: {
                  PaperProps: {
                    sx: {
                      "& .MuiMenuItem-root": {
                        fontSize: "0.7rem",
                        minHeight: "28px",
                        padding: "2px 8px",
                      },
                    },
                  },
                },
              }}
            >
              {[
                { value: "all", label: "All" },
                { value: "today", label: "Today" },
                { value: "thisWeek", label: "This Week" },
                { value: "thisMonth", label: "This Month" },
                { value: "ytd", label: "Year To Date" },
                { value: "custom", label: "Custom" },
              ].map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>

            {/* Custom Date Pickers */}
            {dateRange === "custom" && (
              <>
                <TextField
                  type="date"
                  label="From"
                  size="small"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: 120 }}
                />
                <TextField
                  type="date"
                  label="To"
                  size="small"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: 120 }}
                />
              </>
            )}

            {/* Buttons */}
            <Button
              variant="contained"
              size="small"
              onClick={handleApply}
              sx={{
                fontSize: "0.7rem",
                px: 1.5,
                py: 0.3,
                height: 32,
                backgroundColor: "#009db5",
                color: "white",
              }}
            >
              Apply
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={handleClear}
              sx={{
                fontSize: "0.7rem",
                px: 1.5,
                py: 0.3,
                height: 32,
              }}
            >
              Clear
            </Button>

            {/* Action buttons moved here */}
            {isTradeAdminOrOrgAdmin(user) ? (
              <>
                <Button
                  size="small"
                  variant="contained"
                  color="success"
                  sx={{ fontSize: "0.7rem", px: 1.5, py: 0.3, height: 32 }}
                  disabled={
                    !Object.keys(rowSelection).length ||
                    Object.keys(rowSelection).some((rowIndex) => {
                      const row = filteredData[parseInt(rowIndex)];
                      return row?.status === "pending";
                    })
                  }
                  onClick={() => {
                    const selectedIds = Object.keys(rowSelection).map((rowIndex) => {
                      return filteredData[parseInt(rowIndex)].id;
                    });
                    openConfirm(selectedIds, "validated");
                  }}
                >
                  Validate Selected
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  color="error"
                  sx={{ fontSize: "0.7rem", px: 1.5, py: 0.3, height: 32 }}
                  disabled={
                    !Object.keys(rowSelection).length ||
                    Object.keys(rowSelection).some((rowIndex) => {
                      const row = filteredData[parseInt(rowIndex)];
                      return row?.status === "pending";
                    })
                  }
                  onClick={() => {
                    const selectedIds = Object.keys(rowSelection).map((rowIndex) => {
                      return filteredData[parseInt(rowIndex)].id;
                    });
                    openConfirm(selectedIds, "rejected");
                  }}
                >
                  Reject Selected
                </Button>
              </>
            ) : (
              <>
                <Button
                  size="small"
                  variant="contained"
                  color="success"
                  sx={{ fontSize: "0.7rem", px: 1.5, py: 0.3, height: 32 }}
                  disabled={
                    !Object.keys(rowSelection).length ||
                    Object.keys(rowSelection).some((rowIndex) => {
                      const row = filteredData[parseInt(rowIndex)];
                      return (
                        row?.status === "validated" ||
                        row?.status === "rejected"
                      );
                    })
                  }
                  onClick={() => {
                    const selectedIds = Object.keys(rowSelection).map((rowIndex) => {
                      return filteredData[parseInt(rowIndex)].id;
                    });
                    openConfirm(selectedIds, "committed");
                  }}
                >
                  Commit Selected
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  color="warning"
                  sx={{ fontSize: "0.7rem", px: 1.5, py: 0.3, height: 32 }}
                  disabled={
                    !Object.keys(rowSelection).length ||
                    Object.keys(rowSelection).some((rowIndex) => {
                      const row = filteredData[parseInt(rowIndex)];
                      return (
                        row?.status === "validated" ||
                        row?.status === "rejected"
                      );
                    })
                  }
                  onClick={() => {
                    const selectedIds = Object.keys(rowSelection).map((rowIndex) => {
                      return filteredData[parseInt(rowIndex)].id;
                    });
                    openConfirm(selectedIds, "pending");
                  }}
                >
                  Revert to Pending
                </Button>
              </>
            )}

            <Box sx={{ display: 'flex', gap: 0.8 }}>
              <Button
                variant="outlined"
                startIcon={<Download />}
                size="small"
                onClick={exportToCSV}
                disabled={loading || !filteredData || filteredData.length === 0}
                sx={{
                  fontSize: "0.75rem",
                  px: 1.5,
                  py: 0.5,
                  whiteSpace: "nowrap",
                  height: 32,
                }}
              >
                Export CSV
              </Button>

              <Button
                variant="outlined"
                component="label"
                startIcon={<UploadFile />}
                size="small"
                sx={{
                  fontSize: "0.75rem",
                  px: 1.5,
                  py: 0.5,
                  whiteSpace: "nowrap",
                  height: 32,
                }}
              >
                Upload CSV/Excel
                <input
                  type="file"
                  hidden
                  accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                  onChange={handleFileUpload}
                />
              </Button>
            </Box>
          </Box>

          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
            {/* Status filter chips */}
            {statusFilter.length > 0 ? (
              statusFilter.map((s, idx) => (
                <Chip
                  key={`status-${idx}`}
                  size="small"
                  label={
                    <Box component="span" sx={{ fontSize: "0.7rem" }}>
                      <strong>Status:</strong> {s}
                    </Box>
                  }
                  onDelete={() =>
                    setStatusFilter((prev) => prev.filter((val) => val !== s))
                  }
                />
              ))
            ) : (
              <Chip
                size="small"
                label={
                  <Box component="span" sx={{ fontSize: "0.7rem" }}>
                    <strong>Status:</strong>{" "}
                    {isTradeAdminOrOrgAdmin(user)
                      ? "Committed"
                      : "Pending, Rejected"}
                  </Box>
                }
                sx={{ opacity: 0.7 }}
              />
            )}

            {/* Book filter chips */}
            {bookFilter.length > 0 &&
              bookFilter.map((b, idx) => (
                <Chip
                  key={`book-${idx}`}
                  size="small"
                  label={
                    <Box component="span" sx={{ fontSize: "0.7rem" }}>
                      <strong>Book:</strong> {b}
                    </Box>
                  }
                  onDelete={() =>
                    setBookFilter((prev) => prev.filter((val) => val !== b))
                  }
                />
              ))}

            {/* Commodity filter chips */}
            {commodityFilter.length > 0 &&
              commodityFilter.map((c, idx) => (
                <Chip
                  key={`commodity-${idx}`}
                  size="small"
                  label={
                    <Box component="span" sx={{ fontSize: "0.7rem" }}>
                      <strong>Commodity:</strong> {c}
                    </Box>
                  }
                  onDelete={() =>
                    setCommodityFilter((prev) => prev.filter((val) => val !== c))
                  }
                />
              ))}

            {/* Date usage */}
            {dateUsage !== "formatted_date" && (
              <Chip
                size="small"
                label={
                  <Box
                    component="span"
                    sx={{ fontSize: "0.7rem", textTransform: "capitalize" }}
                  >
                    <strong>Date Usage:</strong> {dateUsage}
                  </Box>
                }
                onDelete={() => setDateUsage("formatted_date")}
              />
            )}

            {/* Date range */}
            {dateRange !== "all" && (
              <Chip
                size="small"
                label={
                  <Box
                    component="span"
                    sx={{ fontSize: "0.7rem", textTransform: "capitalize" }}
                  >
                    <strong>Date Range:</strong> {dateRange}
                  </Box>
                }
                onDelete={() => setDateRange("all")}
              />
            )}

            {/* Custom date */}
            {dateRange === "custom" && dateFrom && dateTo && (
              <Chip
                size="small"
                label={
                  <Box component="span" sx={{ fontSize: "0.7rem" }}>
                    <strong>Custom:</strong> {dateFrom} → {dateTo}
                  </Box>
                }
                onDelete={() => {
                  setDateRange("all");
                  setDateFrom("");
                  setDateTo("");
                }}
              />
            )}
          </Box>
        </Box>
      </Box>
      {/* ---- Side-by-Side Layout ---- */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: 2,
          width: "100%",
        }}
      >
        {/* --- Original Trades --- */}
        <Paper
          variant="outlined"
          sx={{
            flex: uploadedData.length > 0 ? 1 : "auto",
            borderRadius: 1,
            height: "75vh",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <MaterialReactTable
            columns={columns}
            data={filteredData}
            manualPagination
            onPaginationChange={setPagination}
            enableRowSelection
            enableColumnFilters
            enableRowActions
            enableGrouping
            enableColumnPinning
            enableColumnOrdering
            positionActionsColumn="first"
            onRowSelectionChange={setRowSelection}
            enableStickyHeader
            enableStickyFooter
            muiPaginationProps={{
              showRowsPerPage: false
            }}
            state={{
              rowSelection,
              pagination,
              isLoading: loading,
            }}
            initialState={{
              density: "compact",
              pagination: { pageSize: 100, pageIndex: 0 },
              showColumnFilters: true,
            }}
            rowCount={totalCount}
            muiTableContainerProps={{
              sx: {
                backgroundColor: "#fff",
                maxHeight: "calc(75vh - 120px)",
                height: "auto",
                overflowY: "auto",
                overflowX: "auto",
              },

              className: "table-container-wrapper__new",
            }}
            muiTableHeadCellProps={{
              sx: {
                whiteSpace: "normal !important",
                wordBreak: "break-word",
                padding: "6px 10px",
                fontSize: "0.7rem",
                textAlign: "center",
                "& .Mui-TableHeadCell-ResizeHandle": {
                  backgroundColor: "#ddd",
                  width: "3px",
                  borderRadius: "2px",
                  "&:hover": {
                    backgroundColor: "#585bcc",
                  },
                },
              },
            }}
            muiTopToolbarProps={{
              sx: {
                backgroundColor: "#fff",
                padding: "8px",
                // Optional: Style the inner MuiBox-root (css-wsew38) for visual confirmation
                "& .MuiBox-root.css-wsew38": {
                  backgroundColor: "#fff",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                },
              },
              className: "top-toolbar-wrapper__new",
            }}
            muiBottomToolbarProps={{
              sx: {
                backgroundColor: "#fff",
                padding: "8px",
              },
              className: "bottom-toolbar-wrapper__new",
            }}
            muiTableBodyCellProps={{
              sx: {
                fontSize: "0.7rem",
                lineHeight: 2.3,
                whiteSpace: "nowrap",
                px: 0.3,
                py: 0.05,
                borderBottom: "1px solid #eee",
                borderRight: "1px solid #eee",
              },
            }}
            muiTableBodyRowProps={{
              sx: {
                height: "auto",
                "&:hover": { backgroundColor: "#f9f9f9" },
              },
            }}

            renderRowActions={({ row }) => (
              <Box sx={{ display: "flex", gap: 0.5 }}>
                {isTradeAdminOrOrgAdmin(user) ? (
                  <>
                    <Tooltip title="Validate" arrow>
                      <span>
                        <IconButton
                          size="small"
                          color="success"
                          sx={{ p: 0.3, fontSize: "0.9rem" }}
                          disabled={row.original.status === "pending"}
                          onClick={() =>
                            openConfirm([row.original.id], "validated")
                          }
                        >
                          <CheckCircle fontSize="inherit" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Reject" arrow>
                      <span>
                        <IconButton
                          size="small"
                          color="error"
                          sx={{ p: 0.3, fontSize: "0.9rem" }}
                          disabled={row.original.status === "pending"}
                          onClick={() =>
                            openConfirm([row.original.id], "rejected")
                          }
                        >
                          <Cancel fontSize="inherit" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </>
                ) : (
                  <>
                    <Tooltip title="Commit" arrow>
                      <IconButton
                        size="small"
                        color="primary"
                        disabled={
                          row.original.status === "validated" ||
                          row.original.status === "rejected"
                        }
                        onClick={() =>
                          openConfirm([row.original.id], "committed")
                        }
                      >
                        <CheckCircle fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Pending" arrow>
                      <IconButton
                        size="small"
                        color="warning"
                        disabled={
                          row.original.status === "validated" ||
                          row.original.status === "rejected"
                        }
                        onClick={() =>
                          openConfirm([row.original.id], "pending")
                        }
                      >
                        <Cancel fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </>
                )}
              </Box>
            )}
            renderTopToolbarCustomActions={({ table }) => {
              const selectedIds = table
                .getSelectedRowModel()
                .rows.map((r) => r.original.id);
              return (
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1.5,
                    px: 1,
                    py: 0.5,
                    fontSize: "0.625rem",
                    alignItems: "center",
                    backgroundColor: "#fafafa",
                    borderBottom: "1px solid #ddd",
                  }}
                >
                  {/* Status summary */}
                  <Typography variant="caption">
                    Pending Qty: <b>{pendingQuantity.toLocaleString()}</b>
                  </Typography>
                  <Typography variant="caption">
                    Selected Qty: <b>{selectedQuantity.toLocaleString()}</b>
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ display: "flex", alignItems: "center", gap: 0.3 }}
                  >
                    <CheckCircle
                      fontSize="inherit"
                      sx={{ color: "#0d47a1" }}
                    />{" "}
                    Committed Qty: <b>{verificationStats.committed}</b>
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ display: "flex", alignItems: "center", gap: 0.3 }}
                  >
                    <CheckCircle fontSize="inherit" color="success" />{" "}
                    Validated Qty: <b>{verificationStats.validated}</b>
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ display: "flex", alignItems: "center", gap: 0.3 }}
                  >
                    <Cancel fontSize="inherit" color="error" /> Rejected Qty:{" "}
                    <b>{verificationStats.rejected}</b>
                  </Typography>
                  <ConfirmDialog
                    open={confirmState.open}
                    onClose={closeConfirm}
                    onConfirm={handleConfirm}
                    status={confirmState.status}
                    multiple={confirmState.ids.length > 1}
                    user={user}
                  />
                </Box>
              );
            }}
          />
        </Paper>
        {/* --- Uploaded Data --- */}
        {uploadedData.length > 0 && (
          <Paper
            variant="outlined"
            sx={{
              flex: 1,
              borderRadius: 1,
              height: "75vh",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >

            <MaterialReactTable
              columns={uploadedColumnsWithMatch}
              data={applyDateToUploaded ? filteredUploadedData : uploadedData}
              enableRowSelection
              enableColumnFilters
              enableGrouping
              enableColumnPinning
              enableColumnOrdering
              muiPaginationProps={{
                showRowsPerPage: false
              }}
              enableStickyHeader
              enableStickyFooter
              onRowSelectionChange={setUploadedRowSelection}
              state={{
                isLoading: loading,
                rowSelection: uploadedRowSelection,
              }}
              initialState={{
                density: "compact",
                pagination: { pageSize: 25, pageIndex: 0 },
                showColumnFilters: true,
              }}
              renderTopToolbarCustomActions={() => (
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1.5,
                    px: 1,
                    py: 0.5,
                    fontSize: "0.625rem",
                    alignItems: "center",
                    backgroundColor: "transparent",
                    borderBottom: "1px solid #ddd",
                  }}
                >
                  <Typography variant="caption">
                    Pending Qty: <b>{pendingUploadedQty.toLocaleString()}</b>
                  </Typography>
                  <Typography variant="caption">
                    Selected Qty: <b>{selectedUploadedQty.toLocaleString()}</b>
                  </Typography>

                  <IconButton
                    size="small"
                    onClick={() =>
                      downloadUploadedData(
                        applyDateToUploaded ? filteredUploadedData : uploadedData,
                        uploadedColumnsWithMatch,
                        matchedPairs
                      )
                    }
                  >
                    <GetApp fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => setUploadedData([])}>
                    <Close fontSize="small" />
                  </IconButton>
                </Box>
              )}

              muiTableContainerProps={{
                sx: {
                  backgroundColor: "#fff",
                  maxHeight: "calc(75vh - 120px)",
                  height: "auto",
                  overflowY: "auto",
                  overflowX: "auto",
                },

                className: "table-container-wrapper__new",
              }}
              muiTableHeadCellProps={{
                sx: {
                  whiteSpace: "normal !important",
                  wordBreak: "break-word",
                  padding: "6px 10px",
                  fontSize: "0.7rem",
                  textAlign: "center",
                  "& .Mui-TableHeadCell-ResizeHandle": {
                    backgroundColor: "#ddd",
                    width: "3px",
                    borderRadius: "2px",
                    "&:hover": {
                      backgroundColor: "#585bcc",
                    },
                  },
                },
              }}
              muiTableBodyCellProps={{
                sx: {
                  fontSize: "0.7rem",
                  lineHeight: 2.3,
                  whiteSpace: "nowrap",
                  px: 0.3,
                  py: 0.05,
                  borderBottom: "1px solid #eee",
                  borderRight: "1px solid #eee",
                },
              }}
              muiTopToolbarProps={{
                sx: {
                  backgroundColor: "#fff",
                  padding: "8px",
                  // Optional: Style the inner MuiBox-root (css-wsew38) for visual confirmation
                  "& .MuiBox-root.css-wsew38": {
                    backgroundColor: "#fff",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  },
                },
                className: "top-toolbar-wrapper__new",
              }}
              muiBottomToolbarProps={{
                sx: {
                  backgroundColor: "#fff",
                  padding: "8px",
                },
                className: "bottom-toolbar-wrapper__new",
              }}
              muiTableBodyRowProps={({ row }) => {
                const uploadedId = row.index.toString();
                const isMatched = matchedPairs.some(
                  (p) => p.uploadedId === uploadedId
                );
                const isRejected = rejectedPairs.some(
                  (p) => p.uploadedId === uploadedId
                );

                if (isMatched) {
                  return {
                    sx: { backgroundColor: "#c8e6c9", fontWeight: 600 },
                  };
                }
                if (isRejected) {
                  return {
                    sx: { backgroundColor: "#ffcdd2", fontWeight: 600 },
                  };
                }
                return {
                  sx: {
                    height: 22,
                    "&:hover": { backgroundColor: "#f9f9f9" },
                  },
                };
              }}
            />
          </Paper>
        )}
      </Box>
    </div>
  );
};

export default ReconciliationTable;
